import { AppHeader } from '@/components/AppHeader'
import { AppSidebar } from '@/components/AppSidebar'
import { VStack } from '@/components/Atoms'
import { PageError } from '@/components/PageError'
import { PageLoader } from '@/components/PageLoader'
import { ThemeProvider } from '@/components/ThemeProvider'
import { RouteStaticDataPermissions } from '@/hooks/permissions/usePermissions'
import { getMe, useMe } from '@/hooks/queries/useMe'
import {
  handleInvalidSession,
  isAuthError,
  useVerifySession,
  verifySession,
} from '@/hooks/queries/useVerifySession'
import { useIdleTimeout } from '@/hooks/useIdleTimeout'
import { getSessionToken } from '@/stores/session'
import { MILLIS } from '@repo/constants'
import { getUserAccessibleModules, type SwitchableModule } from '@repo/permissions'
import { ScrollArea, SidebarProvider } from '@repo/ui/components'
import {
  CatchBoundary,
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
} from '@tanstack/react-router'
import { useEffect, useRef } from 'react'

export const Route = createFileRoute('/_auth/_modules')({
  component: AuthLayoutComponent,
  pendingComponent: () => <PageLoader global />,
  beforeLoad: async ({ context, location }) => {
    const redirectToLogin = () => {
      throw redirect({
        to: '/login',
        search: {
          redirect: location.href,
        },
      })
    }

    const sessionToken = getSessionToken()
    if (!sessionToken) return redirectToLogin()

    const { queryClient } = context

    try {
      const isVerified = await queryClient.fetchQuery({
        queryKey: ['verifiedSession'],
        queryFn: async ({ signal }) => await verifySession(signal),
        staleTime: MILLIS.SECOND * 10,
        retry: false,
      })

      if (!isVerified) {
        handleInvalidSession()
        return
      }

      const me = await queryClient.fetchQuery({
        queryKey: ['me'],
        queryFn: ({ signal }) => getMe(signal),
        staleTime: MILLIS.MINUTE * 5,
        retry: false,
      })

      const handleRoutePermissions = (
        route: RouteStaticDataPermissions,
        opts: { atLeastOne: boolean } = { atLeastOne: false },
      ) => {
        if (!opts.atLeastOne) {
          if (!context.hasRoutePermissions(me.permissions, route)) {
            throw redirect({
              to: '/unauthorized',
              search: {
                redirect: location.href,
              },
            })
          }
        } else {
          if (!context.hasRouteOneOfPermissions(me.permissions, route)) {
            throw redirect({
              to: '/unauthorized',
              search: {
                redirect: location.href,
              },
            })
          }
        }
      }

      // Module layouts call this so a direct URL into a module the user holds no role in is refused,
      // even on pages that declare no permission of their own (the welcome pages).
      const assertModuleAccess = (module: SwitchableModule) => {
        if (!getUserAccessibleModules(me.roleNames).includes(module)) {
          throw redirect({
            to: '/unauthorized',
            search: {
              redirect: location.href,
            },
          })
        }
      }

      return { handleRoutePermissions, assertModuleAccess }
    } catch (error) {
      if (isAuthError(error)) {
        handleInvalidSession()
        return
      }
      throw error
    }
  },
})

function ErrorComponent(props: { error: Error }) {
  const navigate = useNavigate()

  if ('status' in props.error && props.error.status === 401) {
    console.debug(props.error)
    navigate({
      to: '/not-found',
      search: { redirect: location.href },
    })
  } else if ('status' in props.error && props.error.status === 403) {
    console.debug(props.error)
    navigate({
      to: '/unauthorized',
      search: { redirect: location.href },
    })
  } else {
    console.error(props.error)
    const message = props.error?.message ? props.error.message : ''
    navigate({
      to: '/error',
      search: { redirect: location.href, message },
    })
  }

  return <Outlet />
}

function AuthLayoutComponent() {
  const { data: me, error } = useMe()
  const { error: verifyError, isError: isVerifyError } = useVerifySession()
  const navigate = useNavigate()
  useIdleTimeout()

  const viewportRef = useRef<HTMLDivElement>(null)
  const { pathname } = useLocation()

  useEffect(() => {
    viewportRef.current?.scrollTo(0, 0)
  }, [pathname])

  // useMe is gated on useVerifySession's isSuccess, so a verify error leaves `me` undefined
  // and the `if (!me) return null` check below would render blank. Surface it here instead.
  // Auth errors are excluded: handleInvalidSession already kicked off a login redirect.
  if (isVerifyError && !isAuthError(verifyError)) {
    const message =
      verifyError instanceof Error ? verifyError.message : 'Unable to verify your session.'
    return <PageError message={message} global />
  }
  if (!me) return null
  if (me.permissions && me.permissions.length === 0) navigate({ to: '/unverified' })
  if (error) return <PageError message={error.message} global />

  return (
    <ThemeProvider>
      <div className="text-foreground text-sm subpixel-antialiased">
        <SidebarProvider className="fixed left-0 top-0 w-screen">
          <AppSidebar />

          <VStack className="max-h-svh w-full min-w-0">
            <AppHeader />

            <ScrollArea
              className="bg-background-level-2 h-full w-full flex-1"
              viewportRef={viewportRef}
            >
              <CatchBoundary getResetKey={() => 'reset'} errorComponent={ErrorComponent}>
                <Outlet />
              </CatchBoundary>
            </ScrollArea>
          </VStack>
        </SidebarProvider>
      </div>
    </ThemeProvider>
  )
}
